import React, { useState, useEffect } from "react";
import { Tooltip, Checkbox, Row, Col } from "antd";
import { CSVLink } from "react-csv";
import { toPrice } from "../../../helpers/stringFormat";
import moment from "moment";
import GenericModal from "../../buttons/genericModal";
import Button from '@mui/material/Button';

/**
 * Transaction exporting button and modal. 
 * @param {*} props 
 * @prop {Array.Object} transactions array of transactions objects to export
 * @prop {boolean} pending if True, set selection columns options to the pending options set
 * @prop {string} fileName (optional) set export output file name, defaults to 'transactions.csv'
 */
const TransactionsCsvExport = (props) => {
    const [selectedColumns, setSelectedColumns] = useState({});
    const [visible, setVisible] = useState(false);
    const [csvDownloadAllowed, setCsvDownloadAllowed] = useState(false);
    const [dynamicTagColumns, setDynamicTagColumns] = useState([]);

    const transactionInfoKeys = {
        DATE: true,
        TIME: true,
        DESCRIPTION: true,
        "SHORT DESCRIPTION": true,
        AMOUNT: true,
        BALANCE: true,
        METHOD: true,
        "TO/FROM": true,
        "COUNTERPARTY IS INTERNAL": true,
        "COUNTERPARTY TAGS": true,
        "G/L ITEM": true,
        "G/L ACCT": true,
        NOTES: true,
        TAGS: true,
        EDITOR: true,
        APPROVER: true,
        "CHECK NUMBER": true,
        "REFERENCE NUMBER": true,
    };

    const accountInfoKeys = {
        "ACCOUNT NAME": true,
        "BANK NAME": true,
        "ARPARI ACCOUNT DESCRIPTION": true,
        PURPOSE: true,
        ENTITY: true,
        "ACCOUNT NUMBER LAST 4": true,
    };

    useEffect(() => {
        const hasTransactions = props.transactions && props.transactions.length > 0;
        const hasExportedTransactions = props.exportedTransactions && props.exportedTransactions.length > 0;
        setCsvDownloadAllowed(hasTransactions || hasExportedTransactions);
    }, [props.transactions, props.exportedTransactions]);

    useEffect(() => {
        const tagMap = new Map();

        props.transactions?.forEach((item) => {
            item?.account_generic_tags?.forEach(tag => {
                const group = tag.group_name;
                const tagName = tag.name;
                const parentName = typeof tag.parent === "number" ? "Unknown" : tag.parent?.name;

                const value = parentName ? `${parentName} > ${tagName}` : tagName;

                if (!tagMap.has(group)) {
                    tagMap.set(group, []);
                }

                if (!tagMap.get(group).includes(value)) {
                    tagMap.get(group).push(value);
                }
            });
        });

        const newDynamicTags = Array.from(tagMap.keys());
        setDynamicTagColumns(newDynamicTags);

        const dynamicCols = {};
        newDynamicTags.forEach(group => {
            dynamicCols[group] = true;
        });

        setSelectedColumns(prev => ({
            ...transactionInfoKeys,
            ...accountInfoKeys,
            ...dynamicCols
        }));
    }, [props.transactions]);

    useEffect(() => {
        if (props.pending) {
            setSelectedColumns({
                DATE: true,
                "TO/FROM": true,
                "ACCOUNT NAME": true,
                "BANK NAME": true,
                DESCRIPTION: true,
                "SHORT DESCRIPTION": true,
                AMOUNT: true,
            });
        }
    }, [props.pending]);

    const handleSectionToggle = (sectionKeys, checked) => {
        const updated = { ...selectedColumns };
        Object.keys(sectionKeys).forEach(key => {
            updated[key] = checked;
        });
        if (!props.pending) setSelectedColumns(updated);
    };

    const handleCheckboxChange = (label, value) => {
        setSelectedColumns({ ...selectedColumns, [label]: value });
    };

    const getExportData = () => {
        const transactions = props.transactions || [];
        transactions.sort((a, b) => {
            let timestampA = a?.timestamp?.includes("T") ? a.timestamp : a.timestamp + "T00:00:00";
            let timestampB = b?.timestamp?.includes("T") ? b.timestamp : b.timestamp + "T00:00:00";
            return new Date(timestampB) - new Date(timestampA);
        });

        const hasEditor = transactions.some(item => item?.editor?.length > 0);
        const hasApprover = transactions.some(item => item?.approver?.length > 0);

        return transactions.map(item => {
            const data = {};
            const columnsToExport = props.pending
                ? Object.keys(selectedColumns)
                : Object.keys(selectedColumns).filter(key => selectedColumns[key]);

            columnsToExport.forEach(key => {
                if (selectedColumns[key]) {
                    switch (key) {
                        case "DATE":
                            data.DATE = item?.timestamp?.split("T")[0];
                            break;
                        case "TIME":
                            data.TIME = moment(item?.timestamp).format("h:mm A");
                            break;
                        case "DESCRIPTION":
                            data.DESCRIPTION = item?.description;
                            break;
                        case "SHORT DESCRIPTION":
                            data["SHORT DESCRIPTION"] = item?.description?.slice(0, 40);
                            break;
                        case "AMOUNT":
                            data.AMOUNT = toPrice(item?.amount);
                            break;
                        case "BALANCE":
                            data.BALANCE = item?.balance === "0.00" ? "" : toPrice(item?.balance);
                            break;
                        case "METHOD":
                            data.METHOD = item?.transaction_code;
                            break;
                        case "TO/FROM":
                            data["TO/FROM"] = item?.counterparty_name;
                            break;
                        case "COUNTERPARTY IS INTERNAL":
                            data["COUNTERPARTY IS INTERNAL"] = item?.counterparty?.entity ? "True" : "False"
                            break;
                        case "COUNTERPARTY TAGS":
                            if (item?.counterparty?.generic_tags) {
                              let tagNames = {}
                              item?.counterparty?.generic_tags.forEach((tag) => {
                                let group = tag?.group?.name || 'Unknown Hierarchy' 
                                if (!tagNames[group]) {
                                  tagNames[group] = []
                                }
                                const parentName = typeof tag.parent === "number" ? "Unknown" : tag.parent?.name;
                                tagNames[group] = [...tagNames[group], parentName ? `${parentName} > ${tag.name}` : tag.name]
                              })
                              for (const [group, tags] of Object.entries(tagNames)) {
                                data[group] = tags.join("; ")
                              }
                            }
                            break;
                        case "ACCOUNT NAME":
                            data["ACCOUNT NAME"] = item?.info?.bank_account;
                            break;
                        case "BANK NAME":
                            data["BANK NAME"] = item?.info?.bank?.name;
                            break;
                        case "ARPARI ACCOUNT DESCRIPTION":
                            data["ARPARI ACCOUNT DESCRIPTION"] = item?.account_description;
                            break;
                        case "PURPOSE":
                            data.PURPOSE = item?.info?.bank_account_purpose;
                            break;
                        case "ENTITY":
                            data.ENTITY = item?.info?.entity;
                            break;
                        case "ACCOUNT NUMBER LAST 4":
                            data["ACCOUNT NUMBER LAST 4"] = item?.info?.account_number_last_4;
                            break;
                        case "G/L ITEM":
                            data["G/L ITEM"] = item?.info?.parentcategory;
                            break;
                        case "G/L ACCT":
                            data["G/L ACCT"] = item?.info?.category;
                            break;
                        case "NOTES":
                            data.NOTES = item?.notes;
                            break;
                        case "TAGS":
                            data.TAGS = item?.generic_tags?.map(tag => tag.name).join("; ");
                            break;
                        case "EDITOR":
                            if (hasEditor) data.EDITOR = item?.editor;
                            break;
                        case "APPROVER":
                            if (hasApprover) data.APPROVER = item?.approver;
                            break;
                        case "CHECK NUMBER":
                            data["CHECK NUMBER"] = item?.check_number;
                            break;
                        case "REFERENCE NUMBER":
                            data["REFERENCE NUMBER"] = item?.reference_number;
                            break;
                        default:
                            if (dynamicTagColumns.includes(key)) {
                                const values = item?.account_generic_tags
                                    ?.filter(tag => tag.group_name === key)
                                    ?.map(tag => {
                                        return tag.parent_name ? `${tag.parent_name} > ${tag.name}` : tag.name;
                                    });
                                data[key] = values?.join("; ");
                            }
                            break;
                    }
                }
            });

            return data;
        });
    };

    const exportedTransactions = props.exportedTransactions || getExportData();
    const fileName = props.fileName || "transactions.csv";

    const renderCheckboxGroup = (title, sectionKeys) => {
        const allChecked = Object.keys(sectionKeys).every(key => selectedColumns[key]);
        return (<div
            style={{
                border: '1px solid #ccc',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '20px',
                backgroundColor: '#fafafa',
            }}
        >
            <Row style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                <Checkbox
                    checked={allChecked}
                    onChange={(e) => handleSectionToggle(sectionKeys, e.target.checked)}
                />
                <span style={{ marginLeft: 10 }}>{title}</span>
            </Row>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {Object.keys(sectionKeys).map((key) => (
                    <div
                        key={key}
                        style={{
                            width: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '20px',
                            marginTop: '5px',
                        }}
                    >
                        <Checkbox
                            checked={selectedColumns[key] || false}
                            onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                        />
                        <span style={{ marginLeft: '8px' }}>{key}</span>
                    </div>
                ))}
            </div>
        </div>
        )
    };

    return (
        <>
            <GenericModal
                size="md"
                modalTitle={"Please select the columns you want to export."}
                visible={visible}
                handleCancelModal={() => setVisible(false)}
                showModal={() => setVisible(true)}
                button={
                    <Tooltip title={!csvDownloadAllowed ? "No data to export" : undefined}>
                        <span>
                            <Button
                                disableElevation
                                fullWidth
                                size="small"
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={!csvDownloadAllowed}
                                onClick={csvDownloadAllowed ? () => setVisible(true) : undefined}
                                sx={{
                                    fontSize: 14,
                                    width: '100%',
                                    height: props.height || 'auto',
                                    textTransform: 'none',
                                    cursor: csvDownloadAllowed ? 'pointer' : 'not-allowed',
                                    color: 'white',
                                    border: 0,
                                    textWrap: props.textWrap || 'wrap',
                                }}
                            >
                                Export Data
                            </Button>
                        </span>
                    </Tooltip>
                }
                modalBody={
                    <Col style={{ display: 'flex', flexDirection: 'column' }}>
                        {renderCheckboxGroup("Transaction Information", transactionInfoKeys)}
                        {renderCheckboxGroup("Account Information", { ...accountInfoKeys, ...Object.fromEntries(dynamicTagColumns.map(col => [col, true])) })}
                        <Row justify="center" align="middle" style={{ marginTop: 10 }}>
                            <div
                                style={{
                                    opacity: csvDownloadAllowed ? 1 : 0.5,
                                    pointerEvents: csvDownloadAllowed ? 'auto' : 'none',
                                }}
                            >
                                <CSVLink data={exportedTransactions} filename={fileName}>
                                    <Button
                                        disableElevation
                                        fullWidth
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        style={{
                                            border: 0,
                                            cursor: csvDownloadAllowed ? "pointer" : "not-allowed",
                                        }}
                                        disabled={!csvDownloadAllowed}
                                        sx={{ fontSize: 14, width: '100%', textTransform: 'none' }}
                                    >
                                        Export Data
                                    </Button>
                                </CSVLink>
                            </div>
                        </Row>
                    </Col>
                }
            />
        </>
    );
};

export default TransactionsCsvExport;
